import styles from "./CancelSubscription.module.scss";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetUserSubscriptionCancellationReasonKeysQuery,
  useGetUserSubscriptionQuery,
} from "../../../../../api/queries";
import arrowRight from "../../../../../assets/images/icons/arrow-right-dark.svg";
import { ICancelSubscriptionModel } from "../../../../../models/Stripe";
import ErrorPage from "../../../../ErrorPage/ErrorPage";
import { Form } from "antd";
import CustomButton from "../../../../../components/ui/CustomButton/CustomButton";
import FormItemTextArea from "../../../../../components/ui/FormItemTextArea/FormItemTextArea";
import listMarkIcon from "../../../../../assets/images/icons/list-mark.svg";
import { useNavigate } from "react-router";
import {
  useStripeMutations,
  useUserSubscriptionCancellationReasonMutations,
} from "../../../../../api/mutations";
import useAppDispatch from "../../../../../hooks/useAppDispatch";
import { handleError } from "../../../../../helper/errorHandler";
import FullScreenLoader from "../../../../../components/shared/FullScreenLoader/FullScreenLoader";
import useAppSelector from "../../../../../hooks/useAppSelector";
import { openErrorMessage, openSuccessMessage } from "../../../../../store/message/messageSlice";
import { IUpdateUserSubscriptionCancellationReasonModel } from "../../../../../models/UserSubscription";

enum StageEnum {
  Reason,
  CommentReason,
  BeforeCancel,
  CancelMembership,
  Confirmation,
}

const CancelSubscription = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { data: userSubscription } = useGetUserSubscriptionQuery(user?.id as string);
  const { data } = useGetUserSubscriptionCancellationReasonKeysQuery();
  const keys = data?.keys || [];

  const { cancelSubscription } = useStripeMutations();
  const { updateCancellationReason } = useUserSubscriptionCancellationReasonMutations();

  const [stage, setStage] = useState(StageEnum.Reason);
  const [modelForCancel, setModelForCancel] = useState<ICancelSubscriptionModel>({
    reason: "",
    comment: "",
  });
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState<number | null>(null);

  const getContentByStage = () => {
    switch (stage) {
      case StageEnum.Reason:
        return renderReasonStage();
      case StageEnum.CommentReason:
        return renderCommentReasonStage();
      case StageEnum.BeforeCancel:
        return renderBeforeCancel();
      case StageEnum.CancelMembership:
        return renderCancelMembership();
      case StageEnum.Confirmation:
        return renderConfirmation();
      default:
        return <ErrorPage />;
    }
  };

  const renderReasonStage = () => {
    const clickOnReason = (key: string) => {
      setStage(StageEnum.CommentReason);
      setModelForCancel({ ...modelForCancel, reason: key });
    };

    return (
      <div className={styles.page}>
        <h2 className={styles.title}>
          {t("HelpUsUnderstandYourReasonsForChangingYourMembership")}
        </h2>
        <ul className={styles.reasonsList}>
          {keys.map((key) => {
            return (
              <li onClick={() => clickOnReason(key)} key={key}>
                {t(key)}
                <img src={arrowRight} alt={"Arrow right"} />
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderCommentReasonStage = () => {
    const onFinish = (values: { comment: string }) => {
      setStage(StageEnum.BeforeCancel);
      setModelForCancel({ ...modelForCancel, comment: values.comment });
    };

    return (
      <div className={styles.page}>
        <h2 className={styles.title}>{t("PleaseTellUsAboutYourReasonForCancelling")}</h2>
        <Form
          className={styles.form}
          name="comment-reason"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemTextArea
            name={"comment"}
            label={""}
            rules={{ required: true }}
            placeholder={t("WriteHere")}
          />
          <CustomButton
            style={{ maxWidth: "370px" }}
            text={t("Continue")}
            type={"submit"}
            typeStyle={"fill"}
          />
        </Form>
      </div>
    );
  };

  const renderBeforeCancel = () => {
    return (
      <div className={styles.page}>
        <h2 className={styles.title}>{t("BeforeYouCancel")}</h2>
        <ul className={styles.list}>
          <li>
            <img src={listMarkIcon} alt={"List mark"} />
            {t("PleaseNoteCancellationsTakeEffectImmediately")}
          </li>
          <li>
            <img src={listMarkIcon} alt={"List mark"} />
            {t("ByCancellingYouWillNoLongerBeAbleToMakeAdditionalReservations")}
          </li>
        </ul>
        <div className={styles.actions}>
          <CustomButton
            text={t("KeepMyPlan")}
            type={"button"}
            typeStyle={"fill"}
            style={{
              maxWidth: "370px",
            }}
            functionOnClick={() => {
              navigate("/settings/account/manage-plan");
            }}
          />
          <CustomButton
            text={t("CancelMyMembership")}
            type={"button"}
            typeStyle={"text"}
            style={{
              maxWidth: "370px",
            }}
            functionOnClick={() => {
              setStage(StageEnum.CancelMembership);
            }}
          />
        </div>
      </div>
    );
  };

  const renderCancelMembership = () => {
    return (
      <div className={styles.page}>
        <h2 className={styles.title}>{t("CancelYourMembership")}</h2>
        <p className={styles.subtitle}>
          {t("YourMembershipCancellationWillTakeEffectImmediately")}
        </p>
        <ul className={styles.list}>
          <li>
            <img src={listMarkIcon} alt={"List mark"} />
            {t("AnyUpcomingReservationsYouHaveWillBeAutomaticallyCancelledImmediately")}
          </li>
          <li>
            <img src={listMarkIcon} alt={"List mark"} />
            {t("CancelByJun15ToAvoidAutoRenewingOntoPaidPlan")}
          </li>
          <li>
            <img src={listMarkIcon} alt={"List mark"} />
            {t("RememberYouCanStillEnjoy6CreditsPerMonthBySwitchingToClassPassLite")}
          </li>
        </ul>
        <div className={styles.actions}>
          <CustomButton
            text={t("CancelMyMembership")}
            type={"button"}
            typeStyle={"outline"}
            functionOnClick={() => cancelSubscriptionFn()}
            style={{ maxWidth: "370px" }}
          />
        </div>
      </div>
    );
  };

  const renderConfirmation = () => {
    const onFinish = (values: { additionalComment: string }) => {
      if (!userSubscription) return;
      if (!rating) {
        dispatch(openErrorMessage(t("PleaseSelectRatingBeforeSubmitting")));
        return;
      }
      setLoading(true);
      const model: IUpdateUserSubscriptionCancellationReasonModel = {
        rating,
        additionalComment: values.additionalComment ? values.additionalComment : null,
      };
      updateCancellationReason(
        {
          userSubscriptionId: userSubscription.id,
          model,
        },
        {
          onSuccess: () => {
            setLoading(false);
            navigate("/settings/account/manage-plan");
          },
          onError: (e) => {
            setLoading(false);
            handleError(e, dispatch, t);
          },
        }
      );
    };

    return (
      <div className={styles.page}>
        <h2 className={styles.title}>{t("YourCancellationIsConfirmed")}</h2>
        <div style={{ maxWidth: "466px" }}>
          <p className={styles.ratingLabel}>{t("YourOpinionIsImportantToUs")}</p>

          <p className={styles.subtitle}>{t("HowLikelyAreYouToRecommendClubPassToAFriend")}</p>

          <div className={styles.ratingRow}>
            <span className={styles.ratingHint}>{t("Unlikely")}</span>
            <span className={styles.ratingHint}>{t("VeryLikely")}</span>
          </div>

          <div className={styles.ratingNumbers}>
            {Array.from({ length: 11 }, (_, i) => (
              <label key={i} className={styles.ratingItem}>
                <input
                  type="radio"
                  name="rating"
                  value={i}
                  checked={rating === i}
                  onChange={() => setRating(i)}
                />
                <span>{i}</span>
              </label>
            ))}
          </div>

          <div className={styles.commentRow}>
            <span>{t("Why")}?</span>
            <span>{t("Optional")}</span>
          </div>
          <Form
            className={styles.form}
            name="additional-comment-reason"
            onFinish={onFinish}
            autoComplete="off"
            layout={"vertical"}
          >
            <FormItemTextArea
              name={"additionalComment"}
              label={""}
              rules={{ required: false }}
              placeholder={t("WriteHere")}
            />
            <div className={styles.actions}>
              <CustomButton
                style={{ maxWidth: "370px" }}
                text={t("Submit")}
                type={"submit"}
                typeStyle={"fill"}
              />
              <CustomButton
                style={{ maxWidth: "370px" }}
                text={t("SkipGoToHomepage")}
                type={"button"}
                typeStyle={"text"}
                functionOnClick={() => {
                  navigate("/settings/account/manage-plan");
                }}
              />
            </div>
          </Form>
        </div>
      </div>
    );
  };

  const cancelSubscriptionFn = () => {
    if (!userSubscription) return;
    setLoading(true);
    cancelSubscription(
      { id: userSubscription.id, model: modelForCancel },
      {
        onSuccess: () => {
          setLoading(false);
          setStage(StageEnum.Confirmation);
          dispatch(openSuccessMessage(t("SubscriptionCancelledSuccessfully")));
        },
        onError: (e) => {
          setLoading(false);
          handleError(e, dispatch, t);
        },
      }
    );
  };

  return (
    <>
      {getContentByStage()}
      {loading && <FullScreenLoader />}
    </>
  );
};

export default CancelSubscription;
